import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth, AuthUser, setStoredToken } from '@/shared/auth';
import { ssoLogin } from '@/shared/oidc';
import {
  Loader2,
  AlertTriangle,
  KeyRound,
  ArrowRight,
  Scale,
  Sparkles,
  Search,
  ShieldCheck,
  FileText,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8011';

interface DevUserRow {
  email: string;
  full_name: string;
  role: string;
  satker: string;
}

const ROLE_STYLE: Record<string, { name: string; color: string }> = {
  super_admin:  { name: 'Super Admin',         color: 'text-crimson border-crimson bg-crimson/5' },
  admin:        { name: 'Administrator',       color: 'text-bluestamp border-bluestamp bg-bluestamp/5' },
  auditor:      { name: 'Auditor',             color: 'text-maroon border-maroon bg-maroon/5' },
  penyidik:     { name: 'Penyidik',            color: 'text-bluestamp border-bluestamp bg-bluestamp/5' },
  penyidik_senior: { name: 'Penyidik Senior',  color: 'text-bluestamp border-bluestamp bg-bluestamp/5' },
  analis:       { name: 'Analis',              color: 'text-brass border-brass bg-brass-soft/40' },
  jaksa:        { name: 'JPU',                 color: 'text-maroon border-maroon bg-maroon/5' },
  viewer:       { name: 'Viewer',              color: 'text-ink-2 border-ink-line bg-paper-2/40' },
  privacy_eng:  { name: 'Privacy Eng',         color: 'text-olive border-olive bg-olive/5' },
};

const FEATURES = [
  { Icon: Search,      title: 'Hybrid RAG',       desc: 'BM25 + kNN OpenSearch dengan citation' },
  { Icon: Sparkles,    title: 'OCR + AI',         desc: 'Tesseract scan + 7 modul ekstraksi' },
  { Icon: ShieldCheck, title: 'ABAC + Audit',     desc: 'OPA per-BAP + hash-chain trail' },
  { Icon: FileText,    title: 'Export Indikatif', desc: 'PDF/DOCX dengan PII auto-mask' },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: string } | null)?.from || '/';

  // Auth mode — controls visibility of SSO + dev sections
  const { data: authMode } = useQuery<{ mode: string; local: boolean; oidc: boolean }>({
    queryKey: ['auth-mode'],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/api/v1/auth/mode`);
      if (!resp.ok) throw new Error('mode fetch failed');
      return resp.json();
    },
  });

  // Local (email + password) login state
  const [lEmail, setLEmail] = useState('');
  const [lPassword, setLPassword] = useState('');

  const doLocalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: lEmail, password: lPassword }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(resp.status === 401 ? 'Email atau password salah' : text);
      }
      const data = await resp.json();
      setStoredToken(data.access_token);
      login(data.user as AuthUser);
      navigate(from, { replace: true });
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setIsLoading(false);
    }
  };

  const { data: devUsers } = useQuery<DevUserRow[]>({
    queryKey: ['dev-users'],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/api/v1/auth/dev-users`, {
        headers: { 'X-Dev-User': 'admin@netra.local' },
      });
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
  });

  const doDevLogin = async (email: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/api/v1/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      const user: AuthUser = await resp.json();
      login(user);
      navigate(from, { replace: true });
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-paper text-ink">
      {/* ============================================================ */}
      {/* LEFT — Brand / Value Props                                   */}
      {/* ============================================================ */}
      <aside className="relative hidden lg:flex flex-col p-12 xl:p-16 overflow-hidden text-paper bg-ink">
        {/* Decorative dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, hsl(var(--brass)) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        {/* Brass glow */}
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-brass/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full bg-bluestamp/20 blur-3xl pointer-events-none" />

        {/* Brand */}
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 grid place-items-center border-2 border-brass rounded-full bg-brass-soft/10 relative">
            <Scale className="w-5 h-5 text-brass" />
            <span className="absolute inset-1.5 border border-brass/40 rounded-full" />
          </div>
          <div>
            <div className="font-display font-bold text-[20px] tracking-tight">NETRA</div>
            <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-paper/60">
              Mata Cerdas Berkas Perkara
            </div>
          </div>
        </div>

        {/* Pill */}
        <div className="relative mt-12 inline-flex items-center gap-2 px-3 py-1.5 border border-brass/50 bg-brass/10 text-brass-soft w-fit">
          <Sparkles className="w-3.5 h-3.5" />
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase">AI Document Intelligence</span>
        </div>

        {/* Headline */}
        <h1 className="relative mt-6 font-display font-bold text-[42px] xl:text-[48px] leading-[1.05] tracking-tight max-w-[520px]">
          Analisa <span className="text-brass">Berita Acara Pemeriksaan</span> yang cepat, akurat, dan dapat di-<em className="not-italic underline decoration-brass underline-offset-4">citation</em>.
        </h1>

        <p className="relative mt-5 max-w-[460px] text-paper/70 font-display text-[15px] leading-relaxed">
          Platform on-premise berbasis hybrid RAG + LLM. Upload BAP, sistem otomatis ekstrak temuan,
          pasal indikatif, kontradiksi, kronologi, dan entitas — dengan sumber halaman BAP sebagai citation.
        </p>

        {/* Feature cards 2x2 */}
        <div className="relative mt-10 grid grid-cols-2 gap-3 max-w-[520px]">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="p-4 border border-paper/15 bg-paper/5 backdrop-blur-sm hover:border-brass/50 transition"
            >
              <f.Icon className="w-5 h-5 text-brass mb-2" />
              <div className="font-display font-bold text-[14px] tracking-tight text-paper">{f.title}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-paper/55 mt-1 leading-snug">
                {f.desc}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom row */}
        <div className="relative mt-auto pt-10 flex items-center gap-4 font-mono text-[9px] tracking-[0.2em] uppercase text-paper/40">
          <span>RAG · BM25+kNN</span>
          <span className="w-1 h-1 rounded-full bg-paper/30" />
          <span>OPA ABAC</span>
          <span className="w-1 h-1 rounded-full bg-paper/30" />
          <span>Audit Hash-Chain</span>
          <span className="w-1 h-1 rounded-full bg-paper/30" />
          <span>Self-Hosted LLM</span>
        </div>
      </aside>

      {/* ============================================================ */}
      {/* RIGHT — Sign in form                                          */}
      {/* ============================================================ */}
      <main className="flex flex-col justify-center px-6 sm:px-10 lg:px-14 py-10 overflow-y-auto">
        <div className="w-full max-w-md mx-auto">
          {/* Brand mini (mobile only — left panel hidden) */}
          <div className="flex lg:hidden items-center gap-3 mb-8">
            <div className="w-10 h-10 grid place-items-center border-2 border-brass rounded-full bg-brass-soft">
              <Scale className="w-4 h-4 text-brass" />
            </div>
            <div>
              <div className="font-display font-bold text-[18px] tracking-tight">NETRA</div>
              <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-ink-3">Mata Cerdas Berkas Perkara</div>
            </div>
          </div>

          <h2 className="font-display font-bold text-[30px] tracking-tight">Masuk</h2>
          <p className="mt-1 text-ink-2 font-mono text-[12px] tracking-wide">
            Masuk ke NETRA untuk menganalisa berkas perkara instansi Anda.
          </p>

          {/* Sign-in form */}
          <form onSubmit={doLocalLogin} className="mt-6 space-y-4">
            <div>
              <label htmlFor="email" className="block font-mono text-[10px] uppercase tracking-widest text-ink-2 mb-1.5">
                Email <span className="text-crimson">*</span>
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={lEmail}
                onChange={(e) => setLEmail(e.target.value)}
                placeholder="admin@netra.local"
                className="w-full px-3.5 py-2.5 border border-ink-line bg-paper font-mono text-[13px] focus:outline-none focus:border-brass focus:ring-1 focus:ring-brass transition"
              />
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-1.5">
                <label htmlFor="password" className="font-mono text-[10px] uppercase tracking-widest text-ink-2">
                  Password <span className="text-crimson">*</span>
                </label>
                <span className="font-mono text-[10px] text-brass cursor-not-allowed opacity-50" title="Belum tersedia di build demo">
                  Lupa password?
                </span>
              </div>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={lPassword}
                onChange={(e) => setLPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3.5 py-2.5 border border-ink-line bg-paper font-mono text-[13px] focus:outline-none focus:border-brass focus:ring-1 focus:ring-brass transition"
              />
            </div>

            {error && (
              <div className="p-3 border-2 border-crimson bg-crimson/5 font-mono text-[11px] text-crimson flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !lEmail || !lPassword}
              className="w-full mt-1 px-5 py-3 font-display font-bold text-[13px] tracking-wide bg-brass text-paper hover:bg-brass-2 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Masuk
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* SSO (only when OIDC mode enabled) */}
          {authMode?.oidc && (
            <>
              <div className="my-6 flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-3">
                <span className="flex-1 h-px bg-ink-line" />
                <span>atau lanjut dengan</span>
                <span className="flex-1 h-px bg-ink-line" />
              </div>
              <button
                onClick={() => ssoLogin()}
                className="w-full px-5 py-2.5 font-mono text-[12px] uppercase tracking-wider font-semibold border-2 border-ink hover:bg-ink hover:text-paper transition flex items-center justify-center gap-2"
              >
                <KeyRound className="w-3.5 h-3.5" />
                Sign in via Keycloak (OIDC)
              </button>
            </>
          )}

          {/* Login Cepat — Demo */}
          {devUsers && devUsers.length > 0 && (
            <div className="mt-8 pt-6 border-t border-dashed border-ink-line">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="font-display font-bold text-[14px] tracking-tight">Login Cepat — Demo</h3>
                <span className="font-mono text-[9px] uppercase tracking-widest text-ink-3">{devUsers.length} akun</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {devUsers.map((u) => {
                  const rs = ROLE_STYLE[u.role] || { name: u.role, color: 'text-ink-2 border-ink-line bg-paper-2/40' };
                  return (
                    <button
                      key={u.email}
                      type="button"
                      onClick={() => doDevLogin(u.email)}
                      disabled={isLoading}
                      className={`text-left p-2.5 border ${rs.color} hover:border-brass hover:bg-brass-soft/40 transition disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="font-display font-bold text-[12px] leading-tight truncate">{rs.name}</div>
                      <div className="font-mono text-[9px] text-ink-3 truncate mt-0.5">{u.email.split('@')[0]}</div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-4 font-mono text-[10px] text-ink-3 italic text-center leading-relaxed">
                ⚠ Akun demo hanya tersedia di build dev — disabled di production.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="mt-10 pt-6 border-t border-ink-line text-center font-mono text-[9px] uppercase tracking-[0.18em] text-ink-3">
          © 2026 NETRA · Phase 1 · build {(import.meta.env.MODE || 'dev').toUpperCase()}
        </footer>
      </main>
    </div>
  );
}
